import axios from 'axios';

/**
 * Bitcoin Mempool API 服务
 * 基于 https://mempool.space/api
 */

const BASE_URL = 'https://mempool.space/api';

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求重试配置
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1秒

/**
 * 延迟函数
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 带重试的请求包装器
 */
const requestWithRetry = async (requestFn, retries = MAX_RETRIES) => {
  try {
    return await requestFn();
  } catch (error) {
    if (retries > 0 && error.response?.status >= 500) {
      console.warn(`请求失败，${retries} 次重试剩余...`, error.message);
      await delay(RETRY_DELAY);
      return requestWithRetry(requestFn, retries - 1);
    }
    throw error;
  }
};

/**
 * 获取最新区块高度
 * @returns {Promise<number>} 区块高度
 */
export const getCurrentBlockHeight = async () => {
  try {
    const response = await requestWithRetry(() => 
      apiClient.get('/blocks/tip/height')
    );
    return response.data;
  } catch (error) {
    console.error('获取最新区块高度失败:', error);
    throw new Error('无法获取最新区块高度');
  }
};

/**
 * 获取指定高度的区块列表
 * @param {number} startHeight - 起始高度 (包含该高度及之前的10个区块)
 * @returns {Promise<Array>} 区块列表
 */
export const getBlocks = async (startHeight) => {
  try {
    const endpoint = startHeight ? `/blocks/${startHeight}` : '/blocks';
    const response = await requestWithRetry(() => 
      apiClient.get(endpoint)
    );
    return response.data;
  } catch (error) {
    console.error(`获取区块列表失败 (高度: ${startHeight}):`, error);
    throw new Error('无法获取区块列表');
  }
};

/**
 * 批量获取指定区块范围内的所有区块
 * @param {number} startHeight - 起始高度
 * @param {number} endHeight - 结束高度
 * @returns {Promise<Array>} 区块列表 (按高度降序)
 */
export const getBlocksInRange = async (startHeight, endHeight) => {
  if (startHeight > endHeight) {
    throw new Error('起始高度不能大于结束高度');
  }

  const totalBlocks = endHeight - startHeight + 1;
  const blocks = [];

  try {
    // mempool API 每次返回 10 个区块，从指定高度向前
    // 我们需要从 endHeight 开始，逐步获取到 startHeight
    let currentHeight = endHeight;
    
    while (currentHeight >= startHeight) {
      const batch = await getBlocks(currentHeight);
      
      // 筛选出在目标范围内的区块
      const validBlocks = batch.filter(
        block => block.height >= startHeight && block.height <= endHeight
      );
      
      blocks.push(...validBlocks);
      
      // 如果已经获取足够的区块，退出循环
      if (blocks.length >= totalBlocks) {
        break;
      }
      
      // 获取当前批次中最小的高度，继续向前获取
      const minHeightInBatch = Math.min(...batch.map(b => b.height));
      
      // 如果已经到达或低于起始高度，退出循环
      if (minHeightInBatch <= startHeight) {
        break;
      }
      
      // 下一次请求从 minHeightInBatch - 1 开始
      currentHeight = minHeightInBatch - 1;
      
      // 添加小延迟，避免请求过快
      await delay(300);
    }

    // 按高度升序排序
    blocks.sort((a, b) => a.height - b.height);
    
    // 确保只返回目标范围内的区块
    const filteredBlocks = blocks.filter(
      block => block.height >= startHeight && block.height <= endHeight
    );

    console.log(`成功获取 ${filteredBlocks.length}/${totalBlocks} 个区块 (${startHeight}-${endHeight})`);
    
    return filteredBlocks;
  } catch (error) {
    console.error(`批量获取区块失败 (${startHeight}-${endHeight}):`, error);
    throw error;
  }
};

/**
 * 获取当前轮次的生成区块 (前 48 个区块)
 * @param {number} roundStartHeight - 轮次起始高度
 * @param {number} currentHeight - 当前区块高度
 * @returns {Promise<Array>} 区块列表
 */
export const getRoundGeneratingBlocks = async (roundStartHeight, currentHeight) => {
  const generatingEndHeight = Math.min(currentHeight, roundStartHeight + 47);
  
  try {
    return await getBlocksInRange(roundStartHeight, generatingEndHeight);
  } catch (error) {
    console.error('获取轮次生成区块失败:', error);
    throw error;
  }
};

/**
 * 获取指定幸运数字批次的 6 个区块
 * @param {number} batchStartHeight - 批次起始高度
 * @returns {Promise<Array>} 6 个区块的数组
 */
export const getLuckyNumberBatchBlocks = async (batchStartHeight) => {
  try {
    const blocks = await getBlocksInRange(
      batchStartHeight,
      batchStartHeight + 5
    );
    
    if (blocks.length !== 6) {
      throw new Error(`预期获取 6 个区块，实际获取 ${blocks.length} 个`);
    }
    
    return blocks;
  } catch (error) {
    console.error(`获取幸运数字批次区块失败 (起始高度: ${batchStartHeight}):`, error);
    throw error;
  }
};

/**
 * 获取单个区块详情
 * @param {string} blockHash - 区块哈希
 * @returns {Promise<Object>} 区块详情
 */
export const getBlockByHash = async (blockHash) => {
  try {
    const response = await requestWithRetry(() =>
      apiClient.get(`/block/${blockHash}`)
    );
    return response.data;
  } catch (error) {
    console.error(`获取区块详情失败 (哈希: ${blockHash}):`, error);
    throw new Error('无法获取区块详情');
  }
};

/**
 * 健康检查 - 测试 API 连接
 * @returns {Promise<boolean>} 是否连接成功
 */
export const healthCheck = async () => {
  try {
    await getCurrentBlockHeight();
    return true;
  } catch (error) {
    console.error('API 健康检查失败:', error);
    return false;
  }
};

export default {
  getCurrentBlockHeight,
  getBlocks,
  getBlocksInRange,
  getRoundGeneratingBlocks,
  getLuckyNumberBatchBlocks,
  getBlockByHash,
  healthCheck,
};
